import { Invitation, Inviter, Registerer, RegistererState, SessionState, TransportState, UserAgent, UserAgentState, Web } from 'sip.js'
import { BroadcastMessage, ModeTypes } from 'common/Enums'
import { store } from 'store'
import { isEmpty } from 'lodash'

import WebphoneDelegate from 'common/WebphoneDelegate'
import Broadcast from './Broadcast'
import Logger from './Logger'

import { AppVersion, EnableRtcConnectionStats, SipLogLevel } from './Globals'
import { browserName, browserVersion } from 'react-device-detect'
import { validateRegistationProps } from './Helpers'

import * as sdpTransform from 'sdp-transform'
import { prepareSdpAudio } from './RTCUtils'
import { defaultSize, HubspotCti, HubspotCtiState } from './HubspotCTI'
import { joinConferenceCallNumbers, removeConferenceCallNumber, splitConferenceCallNumberString } from 'utils/numberUtils'
import { initContact } from './WebphonePatches'

RegistererState.Registering = 'Registering'

UserAgent.prototype.initContact = initContact; 

class Webphone {
    constructor() {
        this.delegate = {}

        this.userAgent = null
        this.userAgentOptions = {}
        this.registerer = null
        this.callSessions = new Map()
        this.peerConnection = null
        this.remoteAudioStream = null
        this.audioOutputElement = null
        this.rtcStatsPollTimeout = 1000
        this.click2callHeaderName = 'X-Click-To-Call'
        this.enableRTCConnectionStats = EnableRtcConnectionStats === 'true'
        this.instanceId = this.getInstanceId();
        this.resetRegistration()

        window.debug = this.setDebugUtils()

        return (Webphone.instance = Webphone.instance || this)
    }

    // Create UA and register
    init = (data) => {
        try {
            if (validateRegistationProps(data)) {
                this.delegate = new WebphoneDelegate()
                this.transportOptions = this.getTransportOptions(data)
                this.userAgentOptions = this.getUserAgentOptions(data)
                this.registererOptions = this.getRegistererOptions()
                this.userAgent = new UserAgent(this.userAgentOptions)

                // Create a Registerer to register user agent
                this.createRegisterer()
                this.addRegistererStateChangeHandler()
                this.addUserAgendStateHandlers()
                this.userAgent
                    .start()
                    .then(() => {
                        if(HubspotCtiState.ready) {
                            HubspotCti.initialized({
                                isLoggedIn: !!store.getState().auth.access_token,
                                sizeInfo: defaultSize
                            })
                        }
                        HubspotCtiState.readyToCall.resolve(true)
                    })
                    .catch(this.onError)
            } else {
                throw new Error('Some registration required param is missing')
            }
        } catch (e) {
            Logger.error(e)
        }
    }

    getInstanceId() {
        const key = 'webphone_instance_id';
        let instanceId = localStorage.getItem(key);
        
        if (!instanceId) {
            instanceId = crypto.randomUUID(); // Generate a new UUID
            localStorage.setItem(key, instanceId);
        }
        
        return instanceId;
    }

    get isStarted() {
        return this.userAgent?.state === UserAgentState.Started
    }

    setAudioOutput(audioElement) {
        this.audioOutputElement = audioElement
    }

    unregistered = () => {
        this.registerer && this.registerer.unregistered()
    }

    createRegisterer = () => {
        this.registerer = new Registerer(this.userAgent, this.registererOptions)
    }

    onError = (error) => {
        this.delegate.onConnectionError(null, error.message)
    }

    getUserAgentOptions = ({ extension_number, extension_password, domain_name, contact_nickname }) => {
        return {
            uri: this.makeURI(extension_number, domain_name),
            transportOptions: this.transportOptions,
            authorizationUsername: extension_number,
            authorizationPassword: extension_password,
            userAgentString: `Kavkom Webphone ${AppVersion} - ${browserName} v${browserVersion}`,
            logLevel: SipLogLevel ? SipLogLevel : 'debug',
            viaHost: domain_name,
            contactName: extension_number,
            displayName: contact_nickname,
            delegate: this.getUserAgentDelegate(),
            contactExtras: {
                "+sip.instance": `urn:uuid:${this.instanceId}`
            }
        }
    }

    getTransportOptions = ({ wss_url, port }) => {
        wss_url = `wss://${wss_url}`
        wss_url += parseInt(port) ? `:${port}` : ``
        return { server: wss_url }
    }

    getRegistererOptions = () => {
        return {
            expires: 120,
            refreshFrequency: 50,
            params: {
                toDisplayName: this.userAgentOptions.displayName
            }
        }
    }

    isClick2CallRequest = (session) => {
        return session?.request?.headers.hasOwnProperty(this.click2callHeaderName)
    }

    getUserAgentDelegate = () => {
        return {
            onInvite: (incomingSession) => {
                const number = this.getSessionRemoteNumber(incomingSession)
                const displayName = this.getSessionRemoteName(incomingSession)
                const isClick2Call = this.isClick2CallRequest(incomingSession)

                if (store.getState().settings.autoReject && !isEmpty(this.callSessions)) {
                    incomingSession.reject()
                    return
                }
            
                this.callSessions.set(number, incomingSession)

                if (incomingSession.state === SessionState.Initial) {
                    this.delegate.onIncomingCall({
                        state: SessionState.Initial,
                        number,
                        displayName,
                        isClick2Call
                    })
                    // Setup incoming session delegate
                    incomingSession.delegate = {
                        // Handle incoming REFER request.
                        onRefer(referral) {
                            Logger.log(referral)
                        },
                        onSessionDescriptionHandler: this.onSessionDescriptionHandler
                    }

                    incomingSession.stateChange.addListener((state) =>
                        this.incomingSessionStateChange(state, incomingSession, number, displayName, isClick2Call)
                    )
                }

                if (isClick2Call && store.getState().settings.autoPickup) {
                    incomingSession.accept()
                }
            },
            onConnect: () => {
                this.attemptRegistration()
            },
            onDisconnect: (error) => {
                if (error) {
                    if (this.registerer && !this.registerer.disposed) {
                        this.registerer.dispose() // cleanup registration
                        this.removeRegistererStateChangeHandler()
                    }

                    this.createRegisterer()
                    this.addRegistererStateChangeHandler()
                    this.attemptReconnection()
                }
            }
        }
    }

    resetRegistration = () => {
        this.registrationAttempt = 1 // Start counter
        this.registrationAttempts = 50 // Max tries
        this.registrationDelay = 1 // Interval between attempts in secs
        this.attemptingRegistration = false
    }

    attemptRegistration = () => {
        if (
            this.registerer?.state === RegistererState.Registered ||
            this.userAgent?.transport.state !== TransportState.Connected ||
            store.getState().settings.mode !== ModeTypes.Available
        )
            return

        const { registerer, delegate } = this

        if (this.attemptingRegistration) {
            Logger.log(`Registration attempt already in progress`)
        }

        if (this.registrationAttempt > this.registrationAttempts) {
            delegate.onRegistererStatus(RegistererState.Unregistered)
            delegate.onConnectionError(`Registration maximum attempts reached`)
            return
        }

        if (this.registrationAttempt === 1) {
            Logger.log(`Registration attempt ${this.registrationAttempt} of ${this.registrationAttempts} - trying`)
        } else {
            Logger.log(
                `Registration attempt ${this.registrationAttempt} of ${this.registrationAttempts} - trying in ${this.registrationDelay} seconds`
            )
        }

        this.attemptingRegistration = true

        const register = () => {
            delegate.onRegistererStatus(RegistererState.Registering)

            return registerer.register(this.getRegisterRequestOptions()).catch((error) => {
                console.error(`Error occurred registering after connection with server was obtained.`)
                console.error(error.toString())
            })
        }

        this.registrationTimeout = setTimeout(
            () => {
                if (!registerer.waiting) {
                    register.bind(this).call()
                } else {
                    registerer.waitingChange.addListener(
                        (wait) => {
                            Logger.log('Registerer waiting state changed:' + wait)
                            register.bind(this).call()
                        },
                        { once: true }
                    )
                }
            },
            this.registrationAttempt === 1 ? 0 : this.registrationDelay * 1000
        )
    }

    getRegisterRequestOptions = () => {
        return {
            requestDelegate: {
                onReject: (response) => {
                    Logger.log(
                        `Registration attempt ${this.registrationAttempt} of ${this.registrationAttempts} - failed`
                    )
                    Logger.log(response)
                    if (this.registerer.state !== RegistererState.Registered) {
                        this.attemptingRegistration = false
                        this.registrationAttempt++
                        this.attemptRegistration()
                    }
                },
                onAccept: (response) => {
                    const { Registered } = RegistererState

                    if (
                        this.registerer.status !== Registered ||
                        store.getState().userAgent.registeredStatus !== Registered
                    ) {
                        this.delegate.onRegistererStatus(Registered)
                    }

                    Logger.log(
                        `Registration attempt ${this.registrationAttempt} of ${this.registrationAttempts} - succeeded`
                    )
                    Broadcast.postMessage({
                        type: BroadcastMessage.TabRegistered,
                        payload: true
                    })
                    this.resetRegistration()
                },
                onRedirect: (response) => {
                    console.warn('Registration request redirected')
                    Logger.log(response)
                }
            }
        }
    }

    incomingSessionStateChange = (state, session, number, displayName, isClick2Call) => {
        switch (state) {
            case SessionState.Established:
                this.setupMedia(session)
                this.delegate.onIncomingCall({
                    state,
                    activeCalls: this.activeCalls,
                    number,
                    displayName,
                    isClick2Call
                })
                break
            case SessionState.Terminated:
                this.removeCallSession(number)
                this.delegate.onIncomingCall({
                    state,
                    activeCalls: this.activeCalls,
                    number,
                    displayName,
                    isClick2Call,
                    keepNumber: session.keepNumber
                })
                if (this.activeCalls.length === 0) {
                    this.cleaningUpMedia()
                }
                break
            default:
                this.delegate.onIncomingCall({ state, number, displayName, isClick2Call, session })
                break
        }
    }

    getSessionRemoteNumber = (session) => {
        return session?.remoteIdentity.uri.normal.user
    }

    getSessionRemoteName = (session) => {
        return session?.remoteIdentity.displayName
    }

    getCallSessions = (number) => {
        let sessions = []
    
        if(this.callSessions.has(number)) {
            sessions = this.callSessions.get(number)
        } else {
            // Check if number is conference member and return its session
            const [confSessionNumber, sessionIndex] = this.getConferenceSessionByMember(number)
            sessions = confSessionNumber && sessionIndex >= 0 ? this.callSessions.get(confSessionNumber)[sessionIndex] : sessions
        }
    
        return Array.isArray(sessions) ? sessions : [sessions]
    }

    get hasConferenceSessions() {
        return Array.from(this.callSessions.values()).some(item => Array.isArray(item))
    }
    
    getConferenceSessionByMember(number) {
        if(!this.hasConferenceSessions) return ['', -1]
    
        let sessionIndex = -1
        let conferenceNumber = ''

        for (const num of this.callSessions.keys()) {
            const numbersArray = splitConferenceCallNumberString(num)

            if(numbersArray.includes(number)) {
                sessionIndex = numbersArray.indexOf(number)
                conferenceNumber = num
                break
            }
        }

        return [conferenceNumber, sessionIndex]
    }

    execForSession(number, fn, params = {}) {
        const sessions = this.getCallSessions(number)
    
        if(typeof fn !== 'function' || !sessions) return;

        if(Array.isArray(sessions)) {
            for (const session of sessions) {
                fn(session, params)
            }
        } else {
            fn(sessions, params)
        }
    }

    get activeCalls() {
        return Array.from(this.callSessions.keys())
    }

    addUserAgendStateHandlers = () => {
        this.userAgent.stateChange.addListener((newState) => {
            this.delegate.onUserAgentStatus(newState)
        })

        this.userAgent.transport.stateChange.addListener((newState) => {
            this.delegate.onTransportStatus(newState)
        })
    }

    addRegistererStateChangeHandler = () => {
        this.registerer.stateChange.addListener(this.registererStateChangeListener)
    }

    removeRegistererStateChangeHandler = () => {
        this.registerer.stateChange.removeListener(this.registererStateChangeListener)
    }

    registererStateChangeListener = (state) => {
        this.delegate.onRegistererStatus(state)
    }

    unregister = () => {
        if (this.registerer) {
            this.registerer.unregister()
        }
    }

    // Create target for outcoming call
    createTarget = (number) => {
        const target = this.makeURI(number, this.userAgentOptions.viaHost)
        if (!target) throw new Error('Failed to create target URI.')
        return target
    }

    makeURI = (name, host) => {
        return UserAgent.makeURI(`sip:${name}@${host}`)
    }

    // Create new Inviter
    makeCall = (number, options = {}) => {
        if (this.callSessions.get(number)) {
            this.delegate.onContinueCall()
        } else if (number) {
            const target = this.createTarget(number)

            const inviterOptions = { earlyMedia: true }
            const existingCallId = options?.existingCallId
            if (existingCallId) {
                inviterOptions.extraHeaders = [`X-Kavkom-Call-Id: ${existingCallId}`]
            }

            const inviter = new Inviter(this.userAgent, target, inviterOptions)

            if (inviter.state === SessionState.Initial) {
                this.delegate.onOutgoingCall({
                    state: inviter.state,
                    activeCalls: this.activeCalls,
                    number
                })
            }

            this.callSessions.set(number, inviter)

            inviter.delegate = {
                onSessionDescriptionHandler: this.onSessionDescriptionHandler
            }

            // Handle outgoing session state changes.
            inviter.stateChange.addListener((state) => {
                switch (state) {
                    case SessionState.Established:
                        this.setupMedia(inviter)
                        this.delegate.onOutgoingCall({
                            state,
                            activeCalls: this.activeCalls,
                            number
                        })
                        break
                    case SessionState.Terminated:
                        this.removeCallSession(number)
                        this.delegate.onOutgoingCall({
                            state,
                            activeCalls: this.activeCalls,
                            number,
                            keepNumber: inviter.keepNumber
                        })
                        if (this.activeCalls.length === 0) {
                            this.cleaningUpMedia()
                        }
                        break
                    default:
                        this.delegate.onOutgoingCall({
                            state,
                            activeCalls: this.activeCalls,
                            number
                        })
                        break
                }
            })

            inviter.invite(this.getInvitationAcceptOptions)
        }
    }

    getCallIdForNumber = (number) => {
        const sessions = this.getCallSessions(number)
        const session = Array.isArray(sessions) ? sessions[0] : sessions
        return session?.callId ?? session?.request?.callId ?? null
    }

    activateCallAudio = (number) => {
        this.execForSession(number, (session) => {
            this.setupRemoteAudio(session.sessionDescriptionHandler.remoteMediaStream)
        })
    }

    // Set device id and attach session remote stream to audio tag
    setupMedia = (session) => {
        const sessionDescriptionHandler = session.sessionDescriptionHandler

        if (!sessionDescriptionHandler || !(sessionDescriptionHandler instanceof Web.SessionDescriptionHandler)) {
            throw new Error('Invalid session description handler.')
        }

        if (this.audioOutputElement) {
            const speakerDevice = store.getState().settings.selectedConfigIds.speakersCallDeviceId
            if (speakerDevice && typeof this.audioOutputElement.setSinkId === 'function') {
                this.audioOutputElement
                    .setSinkId(speakerDevice)
                    .then(() => this.setupRemoteAudio(sessionDescriptionHandler.remoteMediaStream))
                    .catch((e) => console.warn(e))
            } else {
                this.setupRemoteAudio(sessionDescriptionHandler.remoteMediaStream)
            }
        }
    }

    // Assign a MediaStream to an HTMLMediaElement and update if tracks change.
    setupRemoteAudio = (stream) => {
        if (this.remoteAudioStream !== stream) {
            // Set element source.

            this.audioOutputElement.autoplay = true // Safari does not allow calling .play() from a non user action
            this.audioOutputElement.srcObject = stream

            // Load and start playback of media.
            this.audioOutputElement.play().catch((error) => {
                console.error('Failed to play media')
                console.error(error)
            })

            // If a track is added, load and restart playback of media.
            stream.onaddtrack = () => {
                this.audioOutputElement.load() // Safari does not work otheriwse
                this.audioOutputElement.play().catch((error) => {
                    console.error('Failed to play remote media on add track')
                    console.error(error)
                })
            }

            // If a track is removed, load and restart playback of media.
            stream.onremovetrack = () => {
                this.audioOutputElement.load() // Safari does not work otheriwse
                this.audioOutputElement.play().catch((error) => {
                    console.error('Failed to play remote media on remove track')
                    console.error(error)
                })
            }

            this.remoteAudioStream = stream
        }
    }

    // Helper function to enable/disable media tracks
    enableReceiverTracks = (session, enable) => {
        if (!session) {
            throw new Error('Session does not exist.')
        }

        const sessionDescriptionHandler = session.sessionDescriptionHandler

        const peerConnection = sessionDescriptionHandler.peerConnection
        if (!peerConnection) {
            throw new Error('Peer connection closed.')
        }

        peerConnection.getReceivers().forEach((receiver) => {
            if (receiver.track) {
                receiver.track.enabled = enable
            }
        })
    }

    // Helper function to enable/disable media tracks
    enableSenderTracks = (session, enable) => {
        if (!session) {
            throw new Error('Session does not exist.')
        }

        const sessionDescriptionHandler = session.sessionDescriptionHandler

        const peerConnection = sessionDescriptionHandler.peerConnection
        if (!peerConnection) {
            throw new Error('Peer connection closed.')
        }

        peerConnection.getSenders().forEach((sender) => {
            if (sender.track) {
                sender.track.enabled = enable
            }
        })
    }

    // Delete stream from audio tag after session end
    cleaningUpMedia = () => {
        if (this.audioOutputElement) {
            this.audioOutputElement.srcObject = null
            this.audioOutputElement.pause()
            this.remoteAudioStream = null
        }
    }

    // Finish session
    hangup = (number, keepNumber = true) => {
        this.execForSession(number, (session) => {
            if (!session?.state) return    
            session.keepNumber = keepNumber

            switch (session.state) {
                case SessionState.Initial:
                    if (session instanceof Inviter) {
                        return session.cancel()
                    } else if (session instanceof Invitation) {
                        return session.reject()
                    }
                    break
                case SessionState.Establishing:
                    if (session instanceof Inviter) {
                        return session.cancel()
                    } else if (session instanceof Invitation) {
                        return session.reject()
                    }
                    break
                case SessionState.Established:
                    return session.bye()
                case SessionState.Terminating:
                    break
                case SessionState.Terminated:
                    break
                default:
                    throw new Error(`Unknown state`)
            }
        })
    }

    // Accept incoming INVITE request.
    acceptCall = (number) => {
        // Handle incoming INVITE request.
        this.callSessions.get(number).accept(this.getInvitationAcceptOptions)
    }

    // reject incoming INVITE request.
    rejectCall = (number) => {
        const session = this.callSessions.get(number)
        if(session) {
            session.reject()
            this.callSessions.delete(number)
        }
    }

    // Toggle Hold call by Phone Number
    toggleHold(number) {
        const sessions = this.callSessions.get(number)
        const session = Array.isArray(sessions) ? sessions[0] : sessions
        const hold = session.hasOwnProperty('held') ? !session.held : true

        this.setHold(number, hold)
    }

    setHold = (number, hold) => {
        this.execForSession(number, async (session, { hold }) => { 
            await this.setSessionHold(session, hold)
            this.onHoldCompleted(session, hold)
            this.delegate.onHold({ value: session.held, number })
        }, { hold, number })
    }

    onHoldCompleted(session, hold) {
        session.held = hold
        session.muted = false

        this.enableReceiverTracks(session, !session.held)
        this.enableSenderTracks(session, !session.held && !session.muted)
    }

    setSessionHold = (session, hold) => {
        return new Promise((res, rej) => {
            session
                .invite({
                    sessionDescriptionHandlerOptions: { hold },
                    requestDelegate: { 
                        onAccept: () => res({ success: true, hold }), 
                        onReject: () => rej({ success: false, hold }) 
                    }
                })
        })
    }

    // Mute call by Phone Number
    toggleMute = (number) => {
        this.execForSession(number, (session) => {
            this.enableSenderTracks(session, !session.held && session.muted)
            session.muted = !session.muted
            this.delegate.onMuteMic({ value: !session.held && session.muted, number })
        })
    }

    // Transfer active call
    transferSession = (session, transferTarget, transferingNumber, transferToNumber) => {
        session.refer(transferTarget, {
            onNotify: (notification) => {
                const body = notification.request.body

                if (body.match(new RegExp(`^SIP/2.0 200 OK`))) {
                    this.delegate.onTransfer({ status: true, number: transferToNumber })
                    this.hangup(transferingNumber, false)
                } else {
                    this.delegate.onTransfer({ status: false, number: transferToNumber })
                }
            }
        })
    }

    transferCall = (transferingNumber, transferToNumber, isTargetSession = false) => {
        if (transferingNumber === transferToNumber) {
            this.delegate.onContinueCall()
            return false
        }
    
        this.execForSession(transferingNumber, (session) => {
            // Send an outgoing REFER request
            let transferTarget = ''

            if(this.callSessions.has(transferToNumber)) {
                transferTarget = this.callSessions.get(transferToNumber)
            } else {
                transferTarget = this.makeURI(transferToNumber, this.userAgentOptions.viaHost)
            }
    
            this.transferSession(session, transferTarget, transferingNumber, transferToNumber)
        })
    }

    // Change active call at multi session
    swap = (currentNumber) => {
        const numbers = this.activeCalls
        const nextNumber = numbers[numbers.indexOf(currentNumber) + 1]
        const number = nextNumber || numbers[0]
        this.setHold(currentNumber, true)
        this.delegate.onChangeActive({ number })
        this.activateCallAudio(number)
        this.peerConnection = this.sessionPeerConnection(this.callSessions.get(number))
    }

    // Send DTMF (tone can be a single character or a string e.g. '*5', '*6')
    dtmf = (number, tone) => {
        const normalized = String(tone).trim()
        if (!normalized || !normalized.match(/^[0-9*#]+$/)) return


        this.execForSession(number, (session) => {
            const sendOne = (char) => {
                const rtpDTMF = session.sessionDescriptionHandler.sendDtmf(char)
                if (!rtpDTMF) {
                    session.info({
                        requestOptions: {
                            body: {
                                contentDisposition: 'render',
                                contentType: 'application/dtmf-relay',
                                content: `Signal=${char}\r\nDuration=1000`
                            }
                        }
                    })
                }
            }
            if (normalized.length === 1) {
                sendOne(normalized)
            } else {
                normalized.split('').forEach((char, i) => {
                    setTimeout(() => sendOne(char), i * 150)
                })
            }
        })
    }

    get getInvitationAcceptOptions() {
        return {
            sessionDescriptionHandlerOptions: this.sessionDescriptionHandlerOptions,
            sessionDescriptionHandlerModifiers: this.sessionDescriptionHandlerModifiers
        }
    }

    get sessionDescriptionHandlerOptions() {
        const inputDeviceId = this.microphoneDeviceId

        const sessionDescriptionHandlerOptions = {
            constraints: {
                audio: { echoCancellation: true },
                video: false
            }
        }

        if (inputDeviceId) sessionDescriptionHandlerOptions.constraints.audio.deviceId = inputDeviceId

        return sessionDescriptionHandlerOptions
    }

    get sessionDescriptionHandlerModifiers() {
        return [this.audioModifier]
    }

    get microphoneDeviceId() {
        return store.getState().settings.selectedConfigIds.microphoneDeviceId
    }

    get peerConnectionDelegate() {
        return {
            onconnectionstatechange: () => {
                switch (this.peerConnection.connectionState) {
                    case 'connected':
                        this.startRtcStatsLogging()
                        break
                    case 'disconnected':
                    case 'failed':
                    case 'closed':
                        this.stopRtcStatsLogging()
                        break
                    default:
                        break
                }
            }
        }
    }

    sessionPeerConnection(session) {
        if (!session) return null
        return session?.sessionDescriptionHandler?.peerConnection
    }

    merge = async () => {
        const sessions = Array.from(this.callSessions.values()).flat();
        const sessionsToUnhold = [];
        const unholdPromises = [];

        for (const session of sessions) {
            if(!session?.held) continue
            sessionsToUnhold.push(session)
            const promise = this.setSessionHold(session, false)
            unholdPromises.push(promise)
        }

        const unholdResults = await Promise.allSettled(unholdPromises)
    
        for (const [index, result] of unholdResults.entries()) {
            const { status, value: { success, hold }} = result
            if(status !== 'fulfilled' || hold || !success) continue
            this.onHoldCompleted(sessionsToUnhold[index])
        }
    
        const numbers = Array.from(this.callSessions.keys())
        const conferenceNumber = joinConferenceCallNumbers(numbers)

        this.callSessions.set(conferenceNumber, sessions)
    
        for (const number of numbers) {
            this.callSessions.delete(number)
        }

        this.createConference(sessions)
        this.delegate.onMerge({ numbers })
    }

    createConference(sessions) {
        // take all received tracks from the sessions you want to merge
        var receiverTracks = [];
    
        sessions.forEach(session => {
            const pc = this.sessionPeerConnection(session)
        
            pc.getReceivers().forEach(receiver => {			
                receiverTracks.push(receiver.track)
            });
        })
    
        // use the Web Audio API to mix the received tracks
        const context = new AudioContext()
        const mixedReceiversMediaStream = new MediaStream()
        
        sessions.forEach(session => {	
            const pc = this.sessionPeerConnection(session)
            const mixedOutput = context.createMediaStreamDestination();
            
            pc.getReceivers().forEach(receiver => {
                receiverTracks.forEach(track => {
                    mixedReceiversMediaStream.addTrack(receiver.track)

                    if(receiver.track.id !== track.id) {
                        const sourceStream = context.createMediaStreamSource(new MediaStream([track]))
                        sourceStream.connect(mixedOutput);
                    }
                })
            })

            // mixing your voice with all the received audio
            pc.getSenders().forEach((sender) => {
                var sourceStream = context.createMediaStreamSource(new MediaStream([sender.track]))
                sourceStream.connect(mixedOutput);
            })

            pc.getSenders()[0].replaceTrack(mixedOutput.stream.getTracks()[0]);
        });
    
        // play all received stream to you
        this.setupRemoteAudio(mixedReceiversMediaStream)
    }

    // Search for session with such number if its conference call
    removeCallSession(number) {
        if(this.callSessions.get(number)) {
            this.callSessions.delete(number)
        } else {
            const [confSessionNumber, searchedSessionIndex] = this.getConferenceSessionByMember(number)

            if(searchedSessionIndex >= 0) {
                const leftNumbers = removeConferenceCallNumber(confSessionNumber, number)
                const restSessions = this.callSessions.get(confSessionNumber).filter(session => session !== this.callSessions.get(confSessionNumber)[searchedSessionIndex])

                this.callSessions.set(leftNumbers, restSessions)
                this.callSessions.delete(confSessionNumber)
            }
        }
    }

    onSessionDescriptionHandler = (sessionDescriptionHandler) => {
        sessionDescriptionHandler.peerConnectionDelegate = this.peerConnectionDelegate
        this.peerConnection = sessionDescriptionHandler.peerConnection
    }

    audioModifier = (description) => {
        const { type } = description

        if (type === 'offer') {
            const sdp = sdpTransform.parse(description.sdp)
            const audio = sdp.media.find((item) => item.type === 'audio')
            audio && prepareSdpAudio(audio)
            description.sdp = sdpTransform.write(sdp)
        }

        return Promise.resolve(description)
    }

    startRtcStatsLogging = () => {
        this.rtcStatsInterval = this.enableRTCConnectionStats
            ? setInterval(this.pollRtcStats, this.rtcStatsPollTimeout)
            : null
    }

    stopRtcStatsLogging = () => {
        this.rtcStatsInterval = this.rtcStatsInterval ? clearInterval(this.rtcStatsInterval) : 0
    }

    pollRtcStats = async () => {
        if (this.peerConnection && this.peerConnection.connectionState === 'connected') {
            const stats = await this.getAllRtcStats()
            this.logInboundRtpStats(stats)
        } else {
            this.stopRtcStatsLogging()
        }
    }

    logInboundRtpStats = (stats) => {
        // Inbound RTP Stre am stats
        const inboundRtpSrc = this.getStatByType(stats, 'inbound-rtp')
        const outboundRtpSrc = this.getStatByType(stats, 'outbound-rtp')
        const result = {}

        if (inboundRtpSrc) {
            Object.assign(result, { jitter: inboundRtpSrc.jitter })
            Object.assign(result, { jitterBufferDelay: inboundRtpSrc.jitterBufferDelay })
            Object.assign(result, { jitterBufferEmittedCount: inboundRtpSrc.jitterBufferEmittedCount })
            Object.assign(result, { packetsLost: inboundRtpSrc.packetsLost })
            Object.assign(result, { inboundCodec: stats[inboundRtpSrc.codecId] })
            Object.assign(result, { outboundCodec: stats[outboundRtpSrc.codecId] })
        }

        // Output
        const timestamp = Object.values(stats)[0].timestamp
        const time = new Date(timestamp)

        console.warn('RTCConnectionStats:', time)
        Logger.log(stats)

        const style = 'background: green; color: white; padding: 15px; display: block; width: 100%'

        console.warn('Inbound RTP Stream stats:', time)
        Logger.log(
            `%c Jitter: ${result.jitter} 
            \n JitterBufferDelay: ${result.jitterBufferDelay} 
            \n JitterBufferEmittedCount: ${result.jitterBufferEmittedCount} 
            \n Packets Lost: ${result.packetsLost} 
            \n Inbound Codec: ${JSON.stringify(result.inboundCodec)} 
            \n Outbound Codec: ${JSON.stringify(result.outboundCodec)}`,
            style
        )
    }

    getAllRtcStats = async () => {
        const statsReport = await this.peerConnection.getStats(null)

        const stats = {}
        const entries = statsReport.entries()

        // Get general stats object
        for (let entry of entries) {
            Object.assign(stats, { [entry[0]]: entry[1] })
        }

        return stats
    }

    getStatByType = (object, type) => {
        return Object.values(object).find((item) => item.type === type)
    }

    attemptReconnection = (reconnectionAttempt = 1) => {
        const reconnectionAttempts = this.reconnectionAttempts || 150
        const reconnectionDelay = this.reconnectionDelay || 2
        const connectionErrorAttempts = this.connectionErrorAttempts || 3

        if (this.attemptingReconnection) {
            Logger.log(`Reconnection attempt already in progress`)
        }

        if (reconnectionAttempt > reconnectionAttempts) {
            this.delegate.onReconnectionFailed()
            Logger.log(`Reconnection maximum attempts reached`)
            return
        }

        if (reconnectionAttempt === 1) {
            Logger.log(`Reconnection attempt ${reconnectionAttempt} of ${reconnectionAttempts} - trying`)
        } else {
            Logger.log(
                `Reconnection attempt ${reconnectionAttempt} of ${reconnectionAttempts} - trying in ${reconnectionDelay} seconds`
            )
        }

        this.attemptingReconnection = true

        setTimeout(
            () => {
                this.userAgent
                    .reconnect()
                    .then(() => {
                        Logger.log(`Reconnection attempt ${reconnectionAttempt} of ${reconnectionAttempts} - succeeded`)
                        this.attemptingReconnection = false
                        if (!this.attemptingRegistration) this.attemptRegistration()
                    })
                    .catch((error) => {
                        Logger.log(`Reconnection attempt ${reconnectionAttempt} of ${reconnectionAttempts} - failed`)
                        console.error(error.message)
                        this.attemptingReconnection = false
                        if (reconnectionAttempt === connectionErrorAttempts) this.delegate.onConnectionError()
                        this.attemptReconnection(++reconnectionAttempt)
                    })
            },
            reconnectionAttempt === 1 ? 0 : reconnectionDelay * 1000
        )
    }

    // For test purposes, to simulate unexpected ws close with error
    fakeDisconnectionError = () => {
        const transport = this.userAgent.transport

        transport._ws = undefined

        if (transport.state !== TransportState.Disconnected) {
            const error = new Error('Unexpected disconnection (1006)')
            transport.transitionState(TransportState.Disconnected, error)
        }
    }

    setDebugUtils = () => {
        return {
            webphone: this,
            fakeDisconnectionError: this.fakeDisconnectionError
        }
    }
}

export default new Webphone()
